import React, { useReducer, useContext, useEffect, useRef, useCallback } from 'react';
import { IApkForAssign, IGetPaginatedApkForAssign, IItemApkForAssign, IAssignApk } from 'types';
import { allApkTypes } from 'appConstants';
import { IAppContext, AppContext } from 'appState/appContext';
import { apkAssignmentReducer } from './apkAssignmentReducer';
import { ApkAssignmentActionTypes } from './apkAssignmentActions';
import {
	apkAssignmentInitialState,
	ApkAssignmentContext,
	TActiveItemToDisplay,
	TTypeOfModalWindow,
	TIDSelectedTypeApk,
	IIDsSelectedItems,
	IApkWithAdditionalProperty,
} from './apkAssignmentContext';
import {
	getPaginatedApkForAssign,
	getCustomersForAssignApk,
	getRestaurantsByIDsCustomersForApk,
	getRestaurantsByIDsRestaurantsForApk,
	assignApk,
} from 'backend/apk';
import { useFetchData } from 'hooks/useFetchData';
import { AxiosResponse } from 'axios';
import { createAppError } from 'utils/helpers';

const ApkAssignmentState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(apkAssignmentReducer, apkAssignmentInitialState);
	const { showLoader, hideLoader, addAppError } = useContext<IAppContext>(AppContext);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IApkForAssign>, IGetPaginatedApkForAssign>(
		isMounted.current ? getPaginatedApkForAssign : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString,
			type: state.IDSelectedTypeApk,
			unassigned: state.isUnassignedApk ? state.isUnassignedApk : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: ApkAssignmentActionTypes.SET_APK_FOR_ASSIGN,
				payload: {
					apkForAssign: response.data,
				},
			});
		}
	}, [response]);

	const changeSearchString = (value: string) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_SEARCH_STRING,
			payload: {
				searchString: value,
			},
		});
	};

	const changeSearchStringForAllCustomers = useCallback((value: string) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_ALL_CUSTOMERS,
			payload: {
				searchStringForAllCustomers: value,
			},
		});
	}, []);

	const changeSearchStringForRestaurants = useCallback((value: string) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_RESTAURANTS,
			payload: {
				searchStringForRestaurants: value,
			},
		});
	}, []);

	const changeSearchStringForApk = useCallback((value: string) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_SEARCH_STRING_FOR_APK,
			payload: {
				searchStringForApk: value,
			},
		});
	}, []);

	const changeTypeApk = (value: TIDSelectedTypeApk) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_TYPE_APK,
			payload: {
				IDSelectedTypeApk: value,
			},
		});
	};

	const resetSelectedControls = () => dispatch({ type: ApkAssignmentActionTypes.RESET_SELECTED_CONTROLS });

	const changeIsUnassignedApkStatus = (value: boolean) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_UNASSIGNED_APK_STATUS,
			payload: {
				isUnassignedApk: value,
			},
		});
	};

	const setIDsSelectedApk = (value: string[]) => {
		dispatch({
			type: ApkAssignmentActionTypes.SET_IDS_SELECTED_APK,
			payload: {
				IDsSelectedApk: value,
			},
		});
	};

	const changeActiveItemToDisplay = (value: TActiveItemToDisplay) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_ACTIVE_ITEM_TO_DISPLAY,
			payload: {
				activeItemToDisplay: value,
			},
		});
	};

	const setShowBreadcrumbs = (value?: boolean) => {
		const isShowBreadCrumbs = value !== undefined ? value : !state.isShowBreadcrumbs;

		dispatch({
			type: ApkAssignmentActionTypes.SET_SHOW_BREADCRUMBS,
			payload: {
				isShowBreadCrumbs,
			},
		});
	};

	const setAllCustomers = async () => {
		try {
			showLoader();
			const response = await getCustomersForAssignApk();
			dispatch({
				type: ApkAssignmentActionTypes.SET_ALL_CUSTOMERS,
				payload: {
					allCustomers: response.data.customers,
				},
			});
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedCustomers = async (value: IIDsSelectedItems, isUpdateRestaurantsList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateRestaurantsList) {
				dispatch({
					type: ApkAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: {},
						restaurants: [],
						IDsSelectedRestaurants: {},
						apk: [],
					},
				});
			} else if (isUpdateRestaurantsList) {
				const arrWithCustomerIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsCustomersForApk(arrWithCustomerIDs);
				const allRestaurantIDs = response.data.customers
					.filter((customer) => customer.restaurants.length)
					.map((customer) => customer.restaurants.map((restaurant) => restaurant.restaurantId))
					.flat();
				const updatedIDsSelectedRestaurants = allRestaurantIDs.reduce<IIDsSelectedItems>((acc, key) => {
					if (key in state.IDsSelectedRestaurants) {
						acc[key] = state.IDsSelectedRestaurants[key];
					}
					return acc;
				}, {});
				const updatedApk = state.apk.filter((el) => allRestaurantIDs.includes(el.restaurantId));
				dispatch({
					type: ApkAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: response.data.customers,
						IDsSelectedRestaurants: updatedIDsSelectedRestaurants,
						apk: updatedApk,
					},
				});
			} else {
				// just to open and close tabs with restaurants.
				dispatch({
					type: ApkAssignmentActionTypes.SET_IDS_SELECTED_CUSTOMERS,
					payload: {
						IDsSelectedCustomers: value,
						restaurants: state.restaurants,
						IDsSelectedRestaurants: state.IDsSelectedRestaurants,
						apk: state.apk,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const setIDsSelectedRestaurants = async (value: IIDsSelectedItems, isUpdateApkList: boolean) => {
		try {
			if (Object.keys(value).length === 0 && isUpdateApkList) {
				dispatch({
					type: ApkAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: {},
						apk: [],
					},
				});
			} else if (isUpdateApkList) {
				const arrWithRestaurantIDs = Object.keys(value);
				showLoader();
				const response = await getRestaurantsByIDsRestaurantsForApk(arrWithRestaurantIDs);
				const IDsRestaurantFromState = state.apk.map((el) => el.restaurantId);
				const IDsRestaurantFromResponse = response.data.restaurants.map((el) => el.restaurantId);
				const filteredApkFromState = state.apk.filter((el) =>
					IDsRestaurantFromResponse.includes(el.restaurantId),
				);
				const filteredApkFromResponse = response.data.restaurants
					.filter((el) => !IDsRestaurantFromState.includes(el.restaurantId))
					.map((el) => ({
						restaurantId: el.restaurantId,
						restaurantAddress: el.restaurantAddress,
						isChanged: false,
						tabletApps: el.tabletApps,
					}));
				dispatch({
					type: ApkAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						apk: [...filteredApkFromState, ...filteredApkFromResponse],
					},
				});
			} else {
				// just to open and close tabs with apk.
				dispatch({
					type: ApkAssignmentActionTypes.SET_IDS_SELECTED_RESTAURANTS,
					payload: {
						IDsSelectedRestaurants: value,
						apk: state.apk,
					},
				});
			}
		} catch (err) {
			const error = err as Error;
			const appError = createAppError(error);
			addAppError(appError);
		} finally {
			hideLoader();
		}
	};

	const addSelectedApkToRestaurant = () => {
		const fullDataOfSelectedApk = state.IDsSelectedApk.reduce<IItemApkForAssign[]>((acc, id) => {
			const searchApk = state.apkForAssign.items.find((el) => el.id === id);
			if (searchApk) {
				acc.push(searchApk);
			}
			return acc;
		}, []);

		const IDsOfOpenRestaurantTabs = Object.keys(state.IDsSelectedRestaurants).filter(
			(id) => state.IDsSelectedRestaurants[id] === true,
		);
		const fullDataOfOpenedRestaurantTabs = state.apk.filter((el) =>
			IDsOfOpenRestaurantTabs.includes(el.restaurantId),
		);

		if (fullDataOfOpenedRestaurantTabs.length) {
			if (
				fullDataOfOpenedRestaurantTabs.some(
					(restaurant) => fullDataOfSelectedApk.length + restaurant.tabletApps.length > allApkTypes.length,
				)
			) {
				dispatch({
					type: ApkAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'apkLimit', // You can not transfer more than four apk.
					},
				});
				return;
			}

			const typesOfSelectedApk = fullDataOfSelectedApk.map((el) => el.type);
			const duplicatesOfSelectedApk = typesOfSelectedApk.filter((number, index, numbers) => {
				return numbers.indexOf(number) !== index;
			});
			if (duplicatesOfSelectedApk.length) {
				dispatch({
					type: ApkAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'apkOfSameType', // You can not transfer apk with the same types.
					},
				});
				return;
			}

			const typesOfApkFromOpenRestaurantTabs = fullDataOfOpenedRestaurantTabs.map((el) =>
				el.tabletApps.map((el) => el.type),
			);
			const duplicatesOfUnitedApk = typesOfApkFromOpenRestaurantTabs.map((el) =>
				[...typesOfSelectedApk, ...el].filter((number, index, numbers) => {
					return numbers.indexOf(number) !== index;
				}),
			);

			if (duplicatesOfUnitedApk.some((el) => el.length)) {
				dispatch({
					type: ApkAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
					payload: {
						typeOfModalWindow: 'restaurantAlreadyHasSelectedApkType', // You cannot transfer the type of apk that is already in the restaurant.
					},
				});
				return;
			}

			// console.log('All conditions for pressing the "assign" button are met.');
			const updatedApk = state.apk.map((restaurant) => {
				if (!IDsOfOpenRestaurantTabs.includes(restaurant.restaurantId)) {
					return restaurant;
				}
				const apkFromFullData = fullDataOfSelectedApk.map((apk) => ({
					tabletAppId: apk.id,
					versionName: apk.versionName,
					restaurantAddress: null,
					type: apk.type,
				}));
				return {
					restaurantId: restaurant.restaurantId,
					restaurantAddress: restaurant.restaurantAddress,
					isChanged: true,
					tabletApps: [...restaurant.tabletApps, ...apkFromFullData],
				};
			});
			dispatch({
				type: ApkAssignmentActionTypes.SET_APK,
				payload: {
					apk: updatedApk,
					IDsSelectedApk: [],
				},
			});
		}
	};

	const deleteApkFromRestaurant = (restaurantID: string, apkID: string) => {
		const updatedApk = state.apk.reduce<IApkWithAdditionalProperty[]>((acc, restaurant) => {
			if (restaurant.restaurantId === restaurantID) {
				const filteredApk = restaurant.tabletApps.filter((el) => el.tabletAppId !== apkID);
				acc.push({
					restaurantId: restaurant.restaurantId,
					restaurantAddress: restaurant.restaurantAddress,
					isChanged: true,
					tabletApps: filteredApk,
				});
				return acc;
			}
			acc.push(restaurant);
			return acc;
		}, []);
		dispatch({
			type: ApkAssignmentActionTypes.SET_APK,
			payload: {
				apk: updatedApk,
				IDsSelectedApk: state.IDsSelectedApk,
			},
		});
	};

	const changeTypeOfModalWindow = (value: TTypeOfModalWindow) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	};

	const changeCurrentPage = (page: number) => {
		dispatch({
			type: ApkAssignmentActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: page,
			},
		});
	};

	const resetChanges = () => dispatch({ type: ApkAssignmentActionTypes.RESET_CHANGES });

	const sendChanges = async () => {
		try {
			const dataForAssign = state.apk.reduce<IAssignApk[]>((acc, restaurant) => {
				if (restaurant.isChanged) {
					acc.push({
						restaurantId: restaurant.restaurantId,
						tabletAppIds: restaurant.tabletApps.map((el) => el.tabletAppId),
					});
				}
				return acc;
			}, []);
			showLoader();
			await assignApk(dataForAssign);
			changeTypeOfModalWindow('success');
		} catch (err) {
			changeTypeOfModalWindow('error');
		} finally {
			hideLoader();
		}
	};

	const updateModuleDataAfterSaving = () => {
		refetch();
		setAllCustomers();
	};

	useEffect(() => {
		isMounted.current = true;
		refetch();
	}, [state.currentPage, state.countOfItems, state.searchString, state.IDSelectedTypeApk, state.isUnassignedApk]);

	useEffect(() => {
		if (state.activeItemToDisplay === 'apk' && !state.apk.length) {
			changeActiveItemToDisplay('restaurants');
		} else if (!state.restaurants.length) {
			changeActiveItemToDisplay('customers');
		}
	}, [state.restaurants, state.apk]);

	useEffect(() => {
		setAllCustomers();
	}, []);

	return (
		<ApkAssignmentContext.Provider
			value={{
				changeSearchString,
				changeSearchStringForAllCustomers,
				changeSearchStringForRestaurants,
				changeSearchStringForApk,
				changeTypeApk,
				resetSelectedControls,
				changeIsUnassignedApkStatus,
				setIDsSelectedApk,
				changeActiveItemToDisplay,
				setShowBreadcrumbs,
				setIDsSelectedCustomers,
				setIDsSelectedRestaurants,
				addSelectedApkToRestaurant,
				deleteApkFromRestaurant,
				changeTypeOfModalWindow,
				changeCurrentPage,
				resetChanges,
				sendChanges,
				updateModuleDataAfterSaving,
				apkForAssign: state.apkForAssign,
				searchString: state.searchString,
				searchStringForAllCustomers: state.searchStringForAllCustomers,
				searchStringForRestaurants: state.searchStringForRestaurants,
				searchStringForApk: state.searchStringForApk,
				IDSelectedTypeApk: state.IDSelectedTypeApk,
				isUnassignedApk: state.isUnassignedApk,
				IDsSelectedApk: state.IDsSelectedApk,
				activeItemToDisplay: state.activeItemToDisplay,
				isShowBreadcrumbs: state.isShowBreadcrumbs,
				allCustomers: state.allCustomers,
				IDsSelectedCustomers: state.IDsSelectedCustomers,
				restaurants: state.restaurants,
				IDsSelectedRestaurants: state.IDsSelectedRestaurants,
				apk: state.apk,
				typeOfModalWindow: state.typeOfModalWindow,
				currentPage: state.currentPage,
				countOfItems: state.countOfItems,
			}}>
			{children}
		</ApkAssignmentContext.Provider>
	);
};

export default ApkAssignmentState;
